import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Table, 
  Input, 
  Button, 
  message,
  Typography,
  Avatar,
  Tag
} from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined } from '@ant-design/icons';
import PageLayout from '../../../components/layout/PageLayout';
import videoService from '../../../services/videoService';
import { useUser } from '../../../context/UserContext';

const { Title, Text } = Typography;
const { Search } = Input;

const RatingList = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [videoRatings, setVideoRatings] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} video ratings`
  });
  
  // Filters
  const [filters, setFilters] = useState({
    search: ''
  });

  const getVideoRatings = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      
      const requestParams = {
        perPage: pagination.pageSize,
        currentPage: pagination.current,
        searchText: filters.search,
        ...params
      };
      
      console.log('Fetching video ratings with params:', requestParams);
      const response = await videoService.getAllVideoRatings(requestParams);
      console.log('Video Ratings API Response:', response);
      
      if (response.data && response.data.status) {
        const videoRatingsData = response.data.data?.data || [];
        const paginationData = response.data.data?.pagination || {};
        
        console.log('Video ratings data:', videoRatingsData);
        console.log('Pagination data:', paginationData);
        
        // Ensure videoRatingsData is an array
        if (Array.isArray(videoRatingsData)) {
          setVideoRatings(videoRatingsData);
          setPagination(prev => ({
            ...prev,
            total: paginationData.total || videoRatingsData.length,
            current: paginationData.currentPage || prev.current
          }));
        } else {
          console.error('Video ratings data is not an array:', videoRatingsData);
          setVideoRatings([]);
        }
      } else {
        message.error('Failed to fetch video ratings');
        setVideoRatings([]);
      }
    } catch (error) {
      console.error('Error fetching video ratings:', error);
      message.error('Failed to fetch video ratings');
      setVideoRatings([]);
    } finally {
      setLoading(false);
    }
  }, [filters.search, pagination.pageSize, pagination.current]);

  // Load video ratings data
  useEffect(() => {
    getVideoRatings();
  }, [getVideoRatings]);

  // Handle search
  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
    getVideoRatings({
      searchText: value,
      currentPage: 1
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;
    
    setPagination(prev => ({
      ...prev,
      current,
      pageSize
    }));
    
    getVideoRatings({
      currentPage: current,
      perPage: pageSize,
      searchText: filters.search
    });
  };

  // Handle add video rating
  const handleAddVideoRating = () => {
    navigate('/Video/ratings/addRatings');
  };

  // Handle edit video rating
  const handleEditVideoRating = (vrId) => {
    navigate(`/Video/ratings/editRatings/${vrId}`);
  };

  // Table columns
  const columns = [
    {
      title: 'Rating',
      dataIndex: 'rating_name',
      key: 'rating_name',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.rating_name.localeCompare(b.rating_name),
      render: (text) => (
        <Text strong style={{ fontSize: '14px' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'Parental Control',
      dataIndex: 'is_parental',
      key: 'is_parental',
      width: 150,
      render: (text) => (
        <Text style={{ fontSize: '14px' }}>
          {text || 'N/A'}
        </Text>
      )
    },
    {
      title: 'Status',
      dataIndex: 'vr_is_active',
      key: 'vr_is_active',
      width: 120,
      render: (status) => (
        <Tag color={status === 'Y' ? 'green' : 'red'}>
          {status === 'Y' ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditVideoRating(record.vr_id)}
          size="small"
        >
          Edit
        </Button>
      )
    }
  ];

  // Header content with user info
  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar 
          src={user?.avatar} 
          size={64} 
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">Manage Video Ratings</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Video Ratings List"
      desc="Manage video ratings and their details"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Card>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <Title level={4} style={{ margin: 0 }}>
              Video Ratings ({pagination.total})
            </Title>
            
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddVideoRating}
              size="large"
            >
              Add Video Rating
            </Button>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '16px', 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <Search
              placeholder="Search video ratings..."
              allowClear
              onSearch={handleSearch}
              style={{ width: '50%', minWidth: '200px' }}
              enterButton={<SearchOutlined />}
            />
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={videoRatings}
          rowKey="vr_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
          size="middle"
          locale={{ 
            emptyText: 'No video ratings found',
            filterConfirm: 'OK',
            filterReset: 'Reset',
            filterEmptyText: 'No filters'
          }}
        />
      </Card>
    </PageLayout>
  );
};

export default RatingList;